import { Button, Icon, Menu, MenuItem, Theme } from "@outschool/backpack";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { faCheck, faChevronDown } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import React from "react";

export default function LearnerFilterButton({
  value,
  onChange
}: {
  value: ChildFragmentFragment | null;
  onChange: (learner: ChildFragmentFragment | null) => void;
}) {
  const { t } = useTranslation("client\\components\\search\\LearnerFilter");
  const { currentUser } = useSession();
  const trackEvent = useTrackEvent();
  const options = currentUser?.children || [];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  function handleClose(cb?: () => void) {
    cb?.();
    setAnchorEl(null);
  }

  function handleOpen(e: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(e.currentTarget);
    trackEvent("search_learner_filter_opened");
  }

  function handleSelect(learner: ChildFragmentFragment | null) {
    const selected = learner ? "learner" : "all";
    onChange(learner);
    trackEvent("search_learner_filter_selected", {
      selected
    });
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="contained"
        sx={(theme: Theme) => ({
          borderRadius: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.4em",
          px: 14,
          height: "100%",
          backgroundColor: open ? "grey.700" : "grey.100",
          color: open ? "white" : "grey.900",
          border: "none",
          boxShadow: "none",
          "&:hover:not(:disabled), &:focus:not(:disabled), &:active:not(:disabled)":
            {
              backgroundColor: open ? "grey.700" : "grey.300",
              color: open ? "white" : "grey.900"
            },
          [theme.breakpoints.down("md")]: {
            height: "3.4rem",
            fontSize: "1.3rem",
            px: 9
          }
        })}
      >
        {value?.name
          ? t("For {{learnerName}}", { learnerName: value.name })
          : t("All classes")}
        <Icon
          icon={faChevronDown}
          sx={{
            color: open ? "white" : "grey.900",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-in-out"
          }}
        />
      </Button>
      <Menu
        onClose={() => handleClose()}
        open={open}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 8,
              marginTop: 8
            }
          }
        }}
        MenuListProps={{
          sx: {
            py: 0,
            // eslint-disable-next-line i18next/no-literal-string
            border: "2px solid #E5E5E5",
            borderRadius: 8
          }
        }}
      >
        <LearnerMenuItem
          label={t("All classes")}
          selected={value === null}
          onClick={() => handleClose(() => handleSelect(null))}
        />
        {options.map(child => (
          <LearnerMenuItem
            key={child.uid}
            selected={value?.uid === child.uid}
            label={t("For {{learnerName}}", { learnerName: child.name })}
            onClick={() => handleClose(() => handleSelect(child))}
          />
        ))}
      </Menu>
    </>
  );
}

function LearnerMenuItem({
  label,
  selected,
  onClick
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <MenuItem
      onClick={onClick}
      sx={(theme: Theme) => ({
        minWidth: 145,
        borderRadius: 0,
        color: "grey.950",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        px: 16,
        py: 12,
        fontWeight: 500,
        [theme.breakpoints.down("md")]: {
          fontSize: "1.3rem",
          minHeight: "inherit"
        }
      })}
    >
      {label}
      <Icon
        icon={faCheck}
        sx={{
          visibility: selected ? "visible" : "hidden"
        }}
      />
    </MenuItem>
  );
}
